/**
 * OrderActionBox — the order's to-do and waiting box (docs/features/
 * order-to-invoice/27_action_box.md), drawn by the shared `ActionBoxLayout`
 * (grey and calm unless something is to do). A box in the order's card,
 * between the header and the rail: the box is what needs a person; the chain
 * is where things are.
 *
 * - The folded line is the backend's (`actions.summary`): "⚠ 2 to do · 1
 *   late · 1 waiting on Warehouse", "Nothing to do · 1 waiting on Acme",
 *   "✓ Delivered · 30 Sep".
 * - A to-do row is dot · verb · which thing · one reason · one button; its
 *   button records a delivery line, contacts a supplier or the customer,
 *   sets the order's contact, changes how it is paid, opens a line's Release
 *   or *Change supplier…*, places a held line now, opens a purchase
 *   order's placement page, or opens the repeatable catalogue item whose
 *   expired artwork stopped a line.
 * - A waiting row is who (a heading per party) · what · which thing · what
 *   happened and when it is expected; the customer's rows carry the
 *   approve-by date's dot (amber close, red passed).
 * - The thing links to its row in the chain: `?step=…&line=…&version=…`.
 *
 * The rows, their words, tiers and order all come from the backend (views'
 * `order_action_box.py`); this only lays them out.
 */
import type { ReactNode } from "react";
import { Link } from "react-router";
import type {
  ActionRow,
  ActionThing,
  OrderActionBox as OrderActionBoxModel,
} from "@shared-core/data/order-to-invoice-types";
import {
  artworkRowButton,
  type ArtworkRowButton,
} from "@shared-core/domain/artwork-staff";
import { paths } from "@shared-core/routing/routes";
import { Button } from "../../primitives/button";
import { ActionBoxLayout } from "../../patterns/ActionBoxLayout";

export interface ActionBoxHandlers {
  busy: boolean;
  /** Open the delivery line's record dialog. */
  onRecordDeliveryLine: (deliveryLineId: string) => void;
  /** Open the Contact dialog for a supplier. */
  onContact: (supplierId: string) => void;
  /** Open the Contact {customer} dialog. */
  onContactCustomer: () => void;
  /** Open Set contact. */
  onSetContact: () => void;
  /** Open Change payment method. */
  onChangePaymentMethod: () => void;
  /** Open Record job number for a made line released record-only. */
  onRecordTharsternJob: (lineRef: string) => void;
  /** Open the Release dialog for a line. */
  onRelease: (lineRef: string) => void;
  /** *Place now?* on a line held to place together. */
  onPlaceNow: (lineRef: string) => void;
  /** Open *Change supplier…* for a line. */
  onChangeSupplier: (lineRef: string) => void;
  /** Open a purchase order's placement page (*Place with {supplier}*). */
  onPlaceWithSupplier: (purchaseOrderNo: string) => void;
  /**
   * An artwork row's button (doc 66 §2.2): upload, decide a check warning,
   * accept for the customer. Absent where the page offers none.
   */
  onArtworkAction?: (button: ArtworkRowButton) => void;
}

function Thing({ orderNo, thing }: { orderNo: string; thing: ActionThing }) {
  const { step, line_no, version_label } = thing.link;
  return (
    <Link
      to={paths.orderDetail({
        orderNo,
        step,
        line: line_no ?? undefined,
        version: version_label ?? undefined,
      })}
      className="text-foreground underline decoration-muted-foreground/45 underline-offset-[3px] hover:decoration-current"
    >
      {thing.label}
    </Link>
  );
}

function RowButton({
  row,
  handlers,
}: {
  row: ActionRow;
  handlers: ActionBoxHandlers;
}) {
  const button = row.button;
  if (!button) return null;
  // A line stopped on a repeatable item is fixed on the item: the button opens its page (doc 83 §8).
  if (button.kind === "open_item_artwork" && button.catalogue_item_id) {
    return (
      <Button
        size="sm"
        variant="outline"
        className="w-full max-md:w-auto max-md:min-w-28"
        asChild
      >
        <Link
          to={paths.catalogueItem({ itemId: button.catalogue_item_id })}
          aria-label={`${button.label}: ${row.thing.label}`}
        >
          {button.label}
        </Link>
      </Button>
    );
  }
  // An artwork row's button opens its version's dialog (doc 66 §2.2).
  const artworkButton = artworkRowButton(button);
  const onClick = () => {
    if (artworkButton) return handlers.onArtworkAction?.(artworkButton);
    switch (button.kind) {
      case "contact":
        if (button.supplier_id) handlers.onContact(button.supplier_id);
        return;
      case "contact_customer":
        return handlers.onContactCustomer();
      case "set_contact":
        return handlers.onSetContact();
      case "change_payment_method":
        return handlers.onChangePaymentMethod();
      case "record_tharstern_job":
        if (button.line_ref) handlers.onRecordTharsternJob(button.line_ref);
        return;
      case "release":
        if (button.line_ref) handlers.onRelease(button.line_ref);
        return;
      case "place_now":
        if (button.line_ref) handlers.onPlaceNow(button.line_ref);
        return;
      case "change_supplier":
        if (button.line_ref) handlers.onChangeSupplier(button.line_ref);
        return;
      case "place_with_supplier":
        if (button.purchase_order_no)
          handlers.onPlaceWithSupplier(button.purchase_order_no);
        return;
      case "record_delivered":
      case "record_received":
        if (button.delivery_line_id)
          handlers.onRecordDeliveryLine(button.delivery_line_id);
    }
  };
  return (
    <Button
      size="sm"
      variant="outline"
      // One width on desktop, so the buttons line up; right-aligned on a phone.
      className="w-full max-md:w-auto max-md:min-w-28"
      disabled={handlers.busy}
      onClick={onClick}
      aria-label={`${button.label.replace(/…$/, "")}: ${row.thing.label}`}
    >
      {button.label}
    </Button>
  );
}

export function OrderActionBox({
  orderNo,
  box,
  isExpanded,
  onExpandedChange,
  handlers,
  planned,
}: {
  orderNo: string;
  box: OrderActionBoxModel;
  isExpanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  handlers: ActionBoxHandlers;
  /** The box's planned row groups (previews only): they too open the box. */
  planned?: ReactNode;
}) {
  return (
    <ActionBoxLayout
      summary={box.summary}
      toDo={box.to_do.map((row) => ({
        key: row.id,
        tier: row.tier,
        verb: row.verb,
        thing: <Thing orderNo={orderNo} thing={row.thing} />,
        reason: row.reason,
        button: <RowButton row={row} handlers={handlers} />,
      }))}
      waiting={box.waiting.map((row) => ({
        key: row.id,
        party: row.party,
        verb: row.verb,
        thing: <Thing orderNo={orderNo} thing={row.thing} />,
        when: [row.done, row.next].filter(Boolean).join(" · "),
        isAuto: false,
        tier: row.tier ?? null,
      }))}
      // The backend's summary carries the next milestone when nothing is open.
      next=""
      footer={planned}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
    />
  );
}
